import { useEffect, useCallback } from 'react';
import { useBeforeUnload } from 'react-router-dom';

/**
 * Hook to warn users about unsaved changes before navigating away.
 * Handles both browser navigation (tab close/refresh) and in-app route changes.
 *
 * @param {boolean} hasUnsavedChanges - Whether the form has unsaved changes
 * @param {string} message - Optional custom warning message
 */
export function useUnsavedChanges(hasUnsavedChanges, message = 'You have unsaved changes. Are you sure you want to leave?') {
  // Handle browser tab close / refresh
  useBeforeUnload(
    useCallback(
      (e) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = message;
          return message;
        }
      },
      [hasUnsavedChanges, message]
    )
  );

  // Handle in-app navigation via popstate (back/forward buttons)
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforePopState = () => {
      const confirmLeave = window.confirm(message);
      if (!confirmLeave) {
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handleBeforePopState);

    return () => {
      window.removeEventListener('popstate', handleBeforePopState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges]);
}

export default useUnsavedChanges;
